import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Button } from "../ui";
import { PageContainer } from "./PageContainer";
import { useSupport } from "../../state/supportContext.jsx";
import SelectSupportDrawer from "../support/SelectSupportDrawer.jsx";

import { useLandingRegistration } from "../../lib/useLandingRegistration.js";

const primaryLinks = [
  { label: "Start", to: "/" },
  { label: "Så fungerar det", to: "/sa-fungerar-salespartner" },
  { label: "Provision", to: "/provision-och-fortjanst" },
  { label: "Frågor och svar", to: "/vanliga-fragor" },
  { label: "Kontakt", to: "/contact" },
];

export function Navbar({ cartCount = 0, user, dashboardLink, onLogout }) {
  const registration = useLandingRegistration("inline");
  const [isOpen, setIsOpen] = useState(false);
  const [supportDrawerOpen, setSupportDrawerOpen] = useState(false);
  const { support } = useSupport();

  const closeMenu = () => setIsOpen(false);

  return (
    <header className="bg-spNavy-900">
      <PageContainer>
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-3 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
            onClick={closeMenu}
          >
            <img src="/salespartner-logo.webp" alt="SalesPartner" width="116" height="104" className="sp-logo" />
          </Link>

          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-white/80 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
            onClick={() => setIsOpen((prev) => !prev)}
            aria-label={isOpen ? "Stäng meny" : "Öppna meny"}
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              {isOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>

          <nav className="hidden md:flex items-center gap-6 text-sm text-white/90">
            {primaryLinks.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  isActive
                    ? "text-white font-semibold"
                    : "text-white/80 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-4 text-sm text-white/90">
            <NavLink to="/shop" className={({ isActive }) => isActive ? "text-white font-semibold" : "text-white/80 hover:text-white transition-colors"}>
              Shop
            </NavLink>
            <NavLink to="/checkout" className={({ isActive }) => isActive ? "text-white font-semibold" : "text-white/80 hover:text-white transition-colors"}>
              Varukorg
              <span className="ml-1 inline-flex items-center justify-center rounded-full bg-white/10 px-2 py-0.5 text-xs font-medium text-white">
                {cartCount}
              </span>
            </NavLink>
            {dashboardLink && (
              <NavLink to={dashboardLink} className={({ isActive }) => isActive ? "text-white font-semibold" : "text-white/80 hover:text-white transition-colors"}>
                Dashboard
              </NavLink>
            )}
            {user ? (
              <button onClick={onLogout} className="text-white/80 hover:text-white transition-colors">
                Logga ut
              </button>
            ) : (
              <NavLink to="/login" className={({ isActive }) => isActive ? "text-white font-semibold" : "text-white/80 hover:text-white transition-colors"}>
                Logga in
              </NavLink>
            )}
            <Button as={Link} to={registration.to} onClick={registration.onClick} size="sm" className="bg-spOrange-500 hover:bg-orange-600 text-white focus-visible:ring-2 focus-visible:ring-amber-300">
              Registrera er grupp
            </Button>
          </div>
        </div>
      </PageContainer>

      {isOpen && (
        <div id="mobile-navigation" className="md:hidden bg-spNavy-800 px-4 py-4 space-y-4">
          <nav className="flex flex-col gap-3 text-sm text-white/90">
            {primaryLinks.map((item) => (
              <NavLink key={item.to} to={item.to} className={({ isActive }) => isActive ? "text-white font-semibold" : "text-white/80 hover:text-white transition-colors"} onClick={closeMenu}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex flex-col gap-3 text-sm text-white/90">
            <button
              type="button"
              onClick={() => { setSupportDrawerOpen(true); closeMenu(); }}
              className="inline-flex items-center gap-2 text-left text-white/80 hover:text-white transition-colors"
            >
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-white text-xs font-semibold">
                ❤
              </span>
              {support ? (
                <span>
                  Du stöttar <span className="font-semibold">{support.groupName}, {support.sellerName}</span> (ändra)
                </span>
              ) : (
                <span className="font-medium">Välj vem du stöttar</span>
              )}
            </button>
            <NavLink to="/shop" className={({ isActive }) => isActive ? "text-white font-semibold" : "text-white/80 hover:text-white transition-colors"} onClick={closeMenu}>
              Shop
            </NavLink>
            <NavLink to="/checkout" className={({ isActive }) => isActive ? "text-white font-semibold" : "text-white/80 hover:text-white transition-colors"} onClick={closeMenu}>
              Varukorg
              <span className="ml-1 inline-flex items-center justify-center rounded-full bg-white/10 px-2 py-0.5 text-xs font-medium text-white">
                {cartCount}
              </span>
            </NavLink>
            {dashboardLink && (
              <NavLink to={dashboardLink} className={({ isActive }) => isActive ? "text-white font-semibold" : "text-white/80 hover:text-white transition-colors"} onClick={closeMenu}>
                Dashboard
              </NavLink>
            )}
            {user ? (
              <button onClick={() => { onLogout?.(); closeMenu(); }} className="text-left text-white/80 hover:text-white transition-colors">
                Logga ut
              </button>
            ) : (
              <NavLink to="/login" className={({ isActive }) => isActive ? "text-white font-semibold" : "text-white/80 hover:text-white transition-colors"} onClick={closeMenu}>
                Logga in
              </NavLink>
            )}
            <Button as={Link} to={registration.to} className="bg-spOrange-500 hover:bg-orange-600 text-white" onClick={() => { registration.onClick(); closeMenu(); }}>
              Registrera er grupp
            </Button>
          </div>
        </div>
      )}

      {/* Stöd-info + drawer-trigger på desktop */}
      <div className="hidden md:block bg-spNavy-800">
        <PageContainer className="py-2 flex items-center justify-end">
          <button
            type="button"
            onClick={() => setSupportDrawerOpen(true)}
            className="inline-flex items-center gap-2 text-xs text-white/70 hover:text-white transition-colors"
          >
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-white text-xs font-semibold">
              ❤
            </span>
            {support ? (
              <span>
                Du stöttar{" "}
                <span className="font-semibold">
                  {support.groupName}, {support.sellerName}
                </span>{" "}
                (ändra)
              </span>
            ) : (
              <span className="font-medium">Välj vem du stöttar</span>
            )}
          </button>
        </PageContainer>
      </div>

      <SelectSupportDrawer
        open={supportDrawerOpen}
        onClose={() => setSupportDrawerOpen(false)}
      />
    </header>
  );
}
